<template>
  <section class="w-full overflow-x-hidden">
    <div
      class="relative min-h-[560px] w-full rounded-2xl sm:min-h-[620px] [@media(min-width:850px)]:aspect-[1366/579]"
    >
      <img
        :src="heroImageUrl"
        alt=""
        width="1366"
        height="579"
        loading="eager"
        fetchpriority="high"
        decoding="async"
        class="absolute inset-0 h-full w-full rounded-2xl object-cover"
      />

      <!-- Overlay -->
      <div class="absolute inset-0 rounded-2xl bg-black/45"></div>

      <!-- Content -->
      <div
        class="absolute inset-0 z-10 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8"
      >
        <div class="">
          <div class="relative h-full w-full">
            <!-- Title -->
            <div class="mx-auto w-full max-w-[900px] pt-0 text-center">
              <h1
                class="text-[21px] font-extrabold uppercase tracking-wide text-white sm:text-[26px] md:text-[30px] lg:text-[38px]"
              >
                <span class="flex flex-col justify-center sm:flex-row sm:gap-2">
                  <span>{{ titleBefore }}</span>
                  <span class="text-pink-500">{{ titleAccent }}</span>
                </span>
              </h1>

              <p
                class="mx-auto mt-2 max-w-[300px] text-[11px] leading-5 text-white/80 sm:mt-3 sm:max-w-[560px] sm:text-[12px] sm:leading-6 md:text-[13px] lg:text-[14px]"
              >
                {{ subtitle }}
              </p>
            </div>

            <!-- Fields -->
            <div
              class="hero-fields mt-4 w-full sm:mt-6 [@media(min-width:850px)]:mt-5"
            >
              <div
                class="mx-auto box-border grid w-full min-w-0 max-w-[860px] grid-cols-2 justify-center gap-2.5 sm:gap-3 [@media(min-width:850px)]:flex [@media(min-width:850px)]:flex-row [@media(min-width:850px)]:items-center [@media(min-width:850px)]:gap-5"
              >
                <!-- Location -->
                <div
                  class="col-span-2 flex h-[58px] w-full flex-col justify-center rounded-xl bg-white px-3.5 py-2.5 shadow-[0_8px_22px_rgba(0,0,0,0.16)] sm:h-[72px] sm:px-4 sm:py-4 lg:h-[99px] lg:px-6 lg:py-5 [@media(min-width:850px)]:col-span-1 [@media(min-width:850px)]:h-[86px] [@media(min-width:850px)]:w-[283px] [@media(min-width:850px)]:px-5 [@media(min-width:850px)]:py-4"
                >
                  <p
                    class="text-[10px] font-semibold text-purple-950 lg:text-xs [@media(min-width:850px)]:text-[11px]"
                  >
                    {{ $t("hero.location") }}
                  </p>

                  <div class="mt-2 flex items-center gap-3 sm:mt-3">
                    <svg
                      class="h-5 w-5 text-pink-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
                      />
                    </svg>

                    <input
                      v-model="locationProxy"
                      readonly
                      type="text"
                      :placeholder="$t('hero.where')"
                      class="w-full cursor-pointer bg-transparent text-[13px] font-semibold text-indigo-950 placeholder:text-indigo-950/40 focus:outline-none sm:text-sm"
                      @click="openMapPicker"
                    />
                  </div>
                </div>

                <!-- Middle -->
                <div
                  class="col-span-1 flex h-[58px] w-full flex-col justify-center rounded-xl bg-white px-3.5 py-2.5 shadow-[0_8px_22px_rgba(0,0,0,0.16)] sm:h-[72px] sm:px-4 sm:py-4 lg:h-[99px] lg:px-5 lg:py-5 [@media(min-width:850px)]:h-[86px] [@media(min-width:850px)]:w-[139px] [@media(min-width:850px)]:px-5 [@media(min-width:850px)]:py-4"
                >
                  <p
                    class="text-[10px] font-semibold text-purple-950 lg:text-xs [@media(min-width:850px)]:text-[11px]"
                  >
                    {{ middleLabel }}
                  </p>

                  <div class="mt-2 flex items-center gap-3 sm:mt-3">
                    <svg
                      v-if="mode === 'event'"
                      class="h-5 w-5 text-pink-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M21 10V7a2 2 0 0 0-2-2H5A2 2 0 0 0 3 7v3a2 2 0 0 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 1 0-4zM9 12h6v2H9v-2zm0 4h6v2H9v-2z"
                      />
                    </svg>

                    <svg
                      v-else-if="mode === 'service'"
                      class="h-5 w-5 text-pink-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M22 6.6a6 6 0 0 1-7.9 5.7l-7.6 7.6a2 2 0 0 1-2.8 0l-.7-.7a2 2 0 0 1 0-2.8l7.6-7.6A6 6 0 0 1 17.4 2l-3 3 2.6 2.6 3-3z"
                      />
                    </svg>

                    <svg
                      v-else
                      class="h-5 w-5 text-pink-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M4 22V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v19h-3v-3H7v3H4z"
                      />
                    </svg>

                    <div
                      v-if="mode === 'event' || mode === 'establishment'"
                      class="relative w-full"
                    >
                      <div
                        ref="middleField"
                        class="relative flex w-full items-center gap-2"
                        @click="handleMiddleClick"
                      >
                        <div class="flex-1">
                          <input
                            readonly
                            :value="
                              selectedVals && selectedVals.length
                                ? String(selectedVals.length)
                                : $t('hero.any')
                            "
                            :title="displayMiddleFull"
                            class="w-full cursor-pointer bg-transparent text-center text-[13px] font-semibold text-indigo-950 placeholder:text-indigo-950/40 focus:outline-none sm:text-sm"
                          />
                        </div>

                        <svg
                          class="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-950"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M7 10l5 5 5-5H7z" />
                        </svg>
                      </div>
                    </div>

                    <div v-else-if="mode === 'service'" class="relative w-full">
                      <select
                        v-model="middleProxy"
                        class="w-full appearance-none bg-transparent pr-5 text-sm font-semibold text-indigo-950 focus:outline-none"
                      >
                        <option value="" disabled>
                          {{ middlePlaceholder }}
                        </option>
                        <option
                          v-for="opt in middleOptions"
                          :key="opt.value"
                          :value="opt.value"
                        >
                          {{ opt.label }}
                        </option>
                      </select>

                      <svg
                        class="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-950"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M7 10l5 5 5-5H7z" />
                      </svg>
                    </div>

                    <input
                      v-else
                      v-model="middleProxy"
                      type="text"
                      :placeholder="$t('hero.name')"
                      class="w-full bg-transparent text-sm font-semibold text-indigo-950 placeholder:text-indigo-950/40 focus:outline-none"
                    />
                  </div>
                  <transition name="fade-slide">
                    <TypesMultiSelect
                      v-if="showTypes"
                      :options="middleOptions"
                      :selected="
                        props.middleValue ? props.middleValue.split(',') : []
                      "
                      @click.stop
                      @update:selected="onTypesUpdate"
                      @close="showTypes = false"
                    />
                  </transition>
                </div>

                <!-- Date -->

                <div
                  ref="dateField"
                  class="col-span-1 flex h-[58px] w-full flex-col justify-center rounded-xl bg-white px-3.5 py-2.5 shadow-[0_8px_22px_rgba(0,0,0,0.16)] sm:h-[72px] sm:px-4 sm:py-4 lg:h-[99px] lg:px-6 lg:py-5 [@media(min-width:850px)]:h-[86px] [@media(min-width:850px)]:w-[283px] [@media(min-width:850px)]:px-5 [@media(min-width:850px)]:py-4"
                  @click="handleDateFieldClick"
                >
                  <p
                    class="text-[10px] font-semibold text-purple-950 lg:text-xs [@media(min-width:850px)]:text-[11px]"
                  >
                    {{ $t("hero.date") }}
                  </p>

                  <div class="mt-2 flex items-center gap-3 sm:mt-3">
                    <svg
                      class="h-5 w-5 text-pink-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M7 2h2v2h6V2h2v2h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3V2z"
                      />
                    </svg>

                    <div ref="dateWrapper" class="relative w-full">
                      <input
                        v-model="dateProxy"
                        readonly
                        type="text"
                        :placeholder="$t('hero.addDate')"
                        class="w-full cursor-pointer bg-transparent text-[13px] font-semibold text-indigo-950 placeholder:text-indigo-950/40 focus:outline-none sm:text-sm"
                      />
                    </div>
                  </div>
                  <transition name="fade-slide">
                    <CalendarPicker
                      v-if="showCalendar"
                      :initial-date="props.date"
                      @select="onCalendarSelect"
                      @close="showCalendar = false"
                    />
                  </transition>
                </div>
              </div>
            </div>

            <MapPicker
              v-if="showMapPicker"
              :initial-lat="initialLat"
              :initial-lon="initialLon"
              :initial-radius-km="props.radiusKm"
              @select="onMapSelect"
              @close="closeMapPicker"
            />

            <!-- Selected chips area (space reserved even when empty to avoid layout jump) -->
            <div
              class="mt-2 min-h-[28px] w-full max-w-[90vw] sm:mt-3 sm:min-h-[40px]"
            >
              <div class="mx-auto w-full min-w-0 max-w-[860px]">
                <div
                  class="chips-scroll flex w-full flex-nowrap gap-3 overflow-x-auto"
                  style="min-width: 0"
                >
                  <template v-if="selectedVals.length > 0">
                    <button
                      v-for="val in selectedVals"
                      :key="val"
                      type="button"
                      class="inline-flex max-w-[160px] flex-shrink-0 items-center truncate rounded-md bg-white px-3 py-1 text-xs text-purple-950"
                      @click.stop="removeType(val)"
                    >
                      <span class="truncate leading-none">{{
                        getLabel(val)
                      }}</span>
                      <span
                        class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] text-purple-900"
                        >×</span
                      >
                    </button>
                  </template>
                </div>
              </div>
            </div>

            <!-- Button -->
            <div class="hero-button flex w-full justify-center">
              <div
                class="w-full min-w-0 max-w-[860px] [@media(min-width:850px)]:h-[44px] [@media(min-width:850px)]:w-[178px]"
              >
                <PrimaryButton
                  :text="$t('hero.search')"
                  width="100%"
                  height="42px"
                  :on-click="onSearchClick"
                  class="w-full"
                  data-testid="hero-search"
                />
              </div>
            </div>

            <div class="hero-mobile-spacer [@media(min-width:850px)]:hidden" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import PrimaryButton from "./buttons/PrimaryButton.vue";

const CalendarPicker = defineAsyncComponent(
  () => import("./CalendarPicker.vue"),
);
const TypesMultiSelect = defineAsyncComponent(
  () => import("./TypesMultiSelect.vue"),
);
const MapPicker = defineAsyncComponent(() => import("./MapPicker.vue"));

const { t } = useI18n();

type Mode = "event" | "establishment" | "service";
type Option = { label: string; value: string };
type MapAddress = {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  county?: string;
  state?: string;
  country?: string;
  [key: string]: unknown;
};
type MapSelection = {
  lat: number;
  lon: number;
  displayName: string;
  city?: string;
  country?: string;
  address?: MapAddress;
  radiusKm: number;
};
type LocationSelection = {
  location: string;
  lat: number;
  lon: number;
  city: string;
  country: string;
  address: MapAddress;
  radiusKm: number;
};

type Props = {
  mode: Mode;
  backgroundImageUrl?: string;
  titleBefore?: string;
  titleAccent?: string;
  subtitle?: string;
  location?: string;
  lat?: number | null;
  lon?: number | null;
  radiusKm?: number;
  middleValue?: string;
  date?: string;
  eventOptions?: Option[];
  serviceOptions?: Option[];
  establishmentOptions?: Option[];
  titleTop?: string;
  fieldsTop?: string;
  buttonTop?: string;
};

const props = withDefaults(defineProps<Props>(), {
  titleBefore: "YOUR EVENT,",
  titleAccent: "OUR PASSION",
  subtitle:
    "UPFEST is an all-in-one party planning program that simplifies the process of organizing events, from birthdays to corporate events.",
  location: "",
  middleValue: "",
  radiusKm: 100,
  date: "",
  eventOptions: () => [
    { label: "Type", value: "" },
    { label: "Festival", value: "festival" },
    { label: "Concert", value: "concert" },
  ],
  serviceOptions: () => [
    { label: "Type", value: "" },
    { label: "VIP", value: "vip" },
    { label: "Catering", value: "catering" },
  ],
  establishmentOptions: () => [{ label: "All", value: "" }],
  titleTop: "135px",
  fieldsTop: "265px",
  buttonTop: "405px",
  backgroundImageUrl: "",
  lat: null,
  lon: null,
});

const emit = defineEmits<{
  (
    e: "update:location" | "update:middleValue" | "update:date",
    v: string,
  ): void;
  (e: "select-location", value: LocationSelection): void;
  (e: "search"): void;
}>();

const heroImageUrl = computed(() => {
  const bgByMode: Record<Mode, string> = {
    event: "/images/heroHeaderEventImage.jpg",
    establishment: "/images/heroHeaderEstablishmentImage.jpg",
    service: "/images/heroHeaderEstablishmentImage.jpg",
  };

  return props.backgroundImageUrl || bgByMode[props.mode] || bgByMode.event;
});

const locationProxy = computed({
  get: () => props.location,
  set: (v: string) => emit("update:location", v),
});

const middleProxy = computed({
  get: () => props.middleValue,
  set: (v: string) => emit("update:middleValue", v),
});

const dateProxy = computed({
  get: () => props.date,
  set: (v: string) => emit("update:date", v),
});

// datepicker state
const showCalendar = ref(false);
const dateWrapper = ref<HTMLElement | null>(null);
const dateField = ref<HTMLElement | null>(null);

// types popup state
const showTypes = ref(false);
const middleField = ref<HTMLElement | null>(null);

// map picker state
const showMapPicker = ref(false);
const initialLat = ref<number | null>(props.lat ?? null);
const initialLon = ref<number | null>(props.lon ?? null);

// Keep map start position in sync with parent-provided coords
watch(
  () => props.lat,
  (v) => {
    if (v != null) initialLat.value = v;
  },
);
watch(
  () => props.lon,
  (v) => {
    if (v != null) initialLon.value = v;
  },
);

function openMapPicker() {
  showMapPicker.value = true;
}

function closeMapPicker() {
  showMapPicker.value = false;
}

function onMapSelect(payload: MapSelection) {
  // prefer a short, human-friendly label (like autodetection) when possible
  const addr = payload.address ?? {};
  const locality =
    addr.city ||
    addr.town ||
    addr.village ||
    addr.hamlet ||
    addr.county ||
    addr.state ||
    "";
  const country = payload.country ?? addr.country ?? "";
  const shortLocation = locality
    ? country
      ? `${locality}, ${country}`
      : locality
    : payload.displayName;

  emit("select-location", {
    location: shortLocation,
    lat: payload.lat,
    lon: payload.lon,
    city: payload.city ?? locality,
    country,
    address: addr,
    radiusKm: payload.radiusKm,
  });
  initialLat.value = payload.lat;
  initialLon.value = payload.lon;
  showMapPicker.value = false;
}

function toggleCalendar() {
  showCalendar.value = !showCalendar.value;
}

function toggleTypes() {
  showTypes.value = !showTypes.value;
}

function onClickOutside(e: MouseEvent) {
  // close calendar if click outside its wrapper
  const dateWrapperEl = dateField.value ?? dateWrapper.value;
  if (dateWrapperEl && !dateWrapperEl.contains(e.target as Node)) {
    showCalendar.value = false;
  }
  // close types popup if click outside its wrapper
  const middleEl = middleField.value;
  if (middleEl && !middleEl.contains(e.target as Node)) {
    showTypes.value = false;
  }
}

onMounted(() => window.addEventListener("click", onClickOutside));
onBeforeUnmount(() => window.removeEventListener("click", onClickOutside));

function onCalendarSelect(iso: string) {
  emit("update:date", iso);
  showCalendar.value = false;
}

function onTypesUpdate(vals: string[]) {
  // update middle value as comma-separated string
  emit("update:middleValue", vals.join(","));
}

const middleLabel = computed(() => {
  return t("hero.any");
});

const middlePlaceholder = computed(() => {
  return t("hero.any");
});

const displayMiddleFull = computed(() => {
  if (!props.middleValue) return t("hero.any");
  const vals = props.middleValue.split(",").filter(Boolean);
  const labels = vals.map((v) => {
    const found = middleOptions.value.find((o) => o.value === v);
    return found ? found.label : v;
  });
  return labels.join(", ");
});

const selectedVals = computed(() =>
  props.middleValue ? props.middleValue.split(",").filter(Boolean) : [],
);

function getLabel(val: string) {
  const found = middleOptions.value.find((o) => o.value === val);
  return found ? found.label : val;
}

function removeType(val: string) {
  const remaining = selectedVals.value.filter((v) => v !== val);
  emit("update:middleValue", remaining.join(","));
}

const middleOptions = computed<Option[]>(() => {
  if (props.mode === "event") return props.eventOptions;
  if (props.mode === "service") return props.serviceOptions;
  if (props.mode === "establishment") return props.establishmentOptions || [];
  return [];
});

function onSearchClick() {
  emit("search");
}

function handleDateFieldClick(e: MouseEvent) {
  // if the calendar is open and the click landed inside the calendar, do nothing
  const el = dateField.value;
  if (!el) return;
  const dropdown = el.querySelector(".calendar-dropdown");
  if (dropdown && dropdown.contains(e.target as Node)) return;
  // otherwise toggle the calendar (open/close)
  toggleCalendar();
}

function handleMiddleClick(e: MouseEvent) {
  const el = middleField.value;
  if (!el) return;
  const dropdown = el.querySelector(".calendar-dropdown");
  if (dropdown && dropdown.contains(e.target as Node)) return;
  toggleTypes();
}
</script>

<style scoped>
.calendar-dropdown {
  width: 100%;
}

.fade-slide-enter-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}

.fade-slide-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.chips-scroll {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.chips-scroll::-webkit-scrollbar {
  height: 0px;
}

.chips-scroll:hover::-webkit-scrollbar {
  height: 8px;
}

.chips-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 999px;
}
</style>
